/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/PhoneInspectionSetup.tsx
 * FILE_ID: JOBHUB-QA-PHONE-NEWINSPECT-001
 * PURPOSE: Phone new-inspection row for Templates, Quotes, and Quote Details.
 * Reused from: packages/job-hub/src/jobs/tabs/job-qa/start-templates/QaStartTemplatesTree.tsx (QaStartTemplatesTree)
 */
import { useMemo, useState, type ReactNode } from 'react'
import { Loader2 } from 'lucide-react'
import type { QaClassification } from '../../qa/classification'
import { createQaApi } from '../../qa/qaApi'
import type { JobHubHttpClient } from '../../jobHubDataAdapter'
import { QaStartTemplatesTree } from './start-templates/QaStartTemplatesTree'
import { useQaStartFromTemplates } from './start-templates/useQaStartFromTemplates'

const OPTIONS = [
  ['templates', 'QA using Templates'],
  ['quotes', 'QA using Quotes'],
] as const

export function PhoneInspectionSetup({
  jobId,
  orgId,
  client,
  classification,
  quotes,
  onCreated,
}: {
  jobId: number
  orgId?: number
  client: JobHubHttpClient
  classification: QaClassification
  quotes: ReactNode
  onCreated: (inspectionId: string) => void
}) {
  const [option, setOption] = useState<(typeof OPTIONS)[number][0]>('quotes')
  const api = useMemo(() => createQaApi(client, orgId), [client, orgId])
  const start = useQaStartFromTemplates({
    api,
    jobId,
    classification,
    showBlank: true,
    workspace: {
      selectInspection: onCreated,
      openCreatedInspection: onCreated,
      setAreaMode: () => undefined,
    },
  })
  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap gap-2" role="group" aria-label="Start/Add/Edit">
        {OPTIONS.map(([id, label]) => (
          <button
            key={id}
            type="button"
            aria-pressed={option === id}
            onClick={() => {
              setOption(id)
              if (id === 'templates') start.openPanel()
            }}
            className={`min-h-[44px] rounded-lg px-3 text-sm font-semibold ${
              option === id ? 'bg-primary text-primary-foreground' : 'border border-border'
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      {option === 'quotes' ? quotes : null}
      {option === 'templates' ? (
        <div>
          <div className="mb-2 flex gap-3">
            <button type="button" className="min-h-[44px] text-sm underline" onClick={start.selectAll}>Select all</button>
            <button type="button" className="min-h-[44px] text-sm underline" onClick={start.clearAll}>Clear</button>
          </div>
          {start.loading ? <p className="text-sm text-muted-foreground">Loading templates…</p> : null}
          {start.groups.length > 0 ? (
            <QaStartTemplatesTree
              groups={start.groups}
              detailsById={start.detailsById}
              selection={start.selection}
              areaNames={start.areaNames}
              onAreaName={start.setAreaName}
              onToggleLocation={start.toggleLocation}
              onToggleTemplate={start.toggleTemplate}
              onToggleSection={start.toggleSection}
            />
          ) : null}
          <button
            type="button"
            className="mt-3 min-h-[44px] rounded-lg bg-primary px-3 text-sm font-medium text-primary-foreground disabled:opacity-40"
            disabled={start.counts.checklists === 0 || start.create.isPending}
            onClick={() => start.create.mutate()}
          >
            {start.create.isPending ? <Loader2 className="mr-1 inline h-3.5 w-3.5 animate-spin" /> : null}
            Create QA
          </button>
          {start.error ? <p className="mt-2 text-xs text-destructive">{start.error}</p> : null}
        </div>
      ) : null}
    </div>
  )
}
